// --- RENDU MARKDOWN AVANCÉ ---
// Module partagé utilisé par AdminAssistant et ReglementationTab

/** Applique le formatage inline (gras, italique, code, liens) */
function formatInline(text: string): string {
  return text
    // Code inline `code`
    .replace(/`([^`]+)`/g, '<code style="background:#f1f5f9;padding:1px 5px;border-radius:4px;font-size:0.88em;color:#1e293b;">$1</code>')
    // Liens [texte](url)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#2563eb;text-decoration:underline;">$1</a>')
    // Gras **texte**
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italique *texte* (attention à ne pas capturer les ** déjà traités)
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
}

/** Détecte si une ligne est un élément structurel (titre, liste, table, séparateur) */
function isStructuralLine(line: string): boolean {
  const trimmed = line.trim();
  if (trimmed === '') return true;
  if (/^#{1,4}\s/.test(trimmed)) return true;
  if (/^[\-\*]\s/.test(trimmed)) return true;
  if (/^\d+[\.\)]\s/.test(trimmed)) return true;
  if (trimmed.startsWith('|') && trimmed.endsWith('|')) return true;
  if (/^-{3,}$/.test(trimmed) || /^\*{3,}$/.test(trimmed)) return true;
  if (trimmed === '--- PAGE ---') return true;
  return false;
}

export function renderMarkdown(text: string): string {
  const lines = text.split('\n');
  const html: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // --- MARQUEUR DE PAGE (extrait PDF) ---
    if (line.trim() === '--- PAGE ---') {
      html.push('<hr style="border:none;border-top:1px dashed #cbd5e1;margin:20px 0;"/>');
      i++;
      continue;
    }

    // --- TABLEAUX MARKDOWN ---
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }
      if (tableLines.length >= 2) {
        const headerCells = tableLines[0].split('|').filter(c => c.trim() !== '');
        const startIdx = tableLines[1].trim().replace(/[|\-:\s]/g, '') === '' ? 2 : 1;
        let tableHtml = '<div style="overflow-x:auto;margin:12px 0;"><table style="width:100%;border-collapse:collapse;font-size:0.88rem;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;">';
        if (startIdx === 2) {
          tableHtml += '<thead><tr>';
          headerCells.forEach(cell => {
            tableHtml += `<th style="background:#f8fafc;padding:10px 14px;text-align:left;font-weight:700;color:#1e293b;border-bottom:2px solid #e2e8f0;white-space:nowrap;">${formatInline(cell.trim())}</th>`;
          });
          tableHtml += '</tr></thead>';
        }
        tableHtml += '<tbody>';
        for (let r = startIdx; r < tableLines.length; r++) {
          const cells = tableLines[r].split('|').filter(c => c.trim() !== '');
          const rowBg = (r - startIdx) % 2 === 0 ? 'white' : '#f8fafc';
          tableHtml += `<tr style="background:${rowBg};">`;
          cells.forEach(cell => {
            tableHtml += `<td style="padding:8px 14px;border-bottom:1px solid #f1f5f9;color:#334155;">${formatInline(cell.trim())}</td>`;
          });
          tableHtml += '</tr>';
        }
        tableHtml += '</tbody></table></div>';
        html.push(tableHtml);
      }
      continue;
    }

    // --- SÉPARATEURS ---
    if (/^-{3,}$/.test(line.trim()) || /^\*{3,}$/.test(line.trim())) {
      html.push('<hr style="border:none;border-top:1px solid #e2e8f0;margin:16px 0;"/>');
      i++;
      continue;
    }

    // --- TITRES ---
    const h2Match = line.match(/^## (.+)$/);
    if (h2Match) {
      html.push(`<h3 style="color:#1e293b;margin:20px 0 10px;font-size:1.1rem;font-weight:700;border-bottom:1px solid #f1f5f9;padding-bottom:6px;">${formatInline(h2Match[1])}</h3>`);
      i++;
      continue;
    }
    const h3Match = line.match(/^### (.+)$/);
    if (h3Match) {
      html.push(`<h4 style="color:#1e293b;margin:16px 0 8px;font-size:1rem;font-weight:700;">${formatInline(h3Match[1])}</h4>`);
      i++;
      continue;
    }
    const h4Match = line.match(/^#### (.+)$/);
    if (h4Match) {
      html.push(`<h5 style="color:#475569;margin:14px 0 6px;font-size:0.95rem;font-weight:700;">${formatInline(h4Match[1])}</h5>`);
      i++;
      continue;
    }
    const h1Match = line.match(/^# (.+)$/);
    if (h1Match) {
      html.push(`<h2 style="color:#1e293b;margin:24px 0 12px;font-size:1.2rem;font-weight:700;">${formatInline(h1Match[1])}</h2>`);
      i++;
      continue;
    }

    // --- LISTES À PUCES ---
    if (/^[\-\*]\s/.test(line.trim())) {
      const items: string[] = [];
      while (i < lines.length && /^[\-\*]\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[\-\*]\s+/, ''));
        i++;
      }
      html.push('<ul style="list-style:disc;padding-left:20px;margin:8px 0;">');
      items.forEach(item => {
        html.push(`<li style="margin:4px 0;color:#334155;">${formatInline(item)}</li>`);
      });
      html.push('</ul>');
      continue;
    }

    // --- LISTES NUMÉROTÉES ---
    if (/^\d+[\.\)]\s/.test(line.trim())) {
      const items: string[] = [];
      while (i < lines.length && /^\d+[\.\)]\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+[\.\)]\s+/, ''));
        i++;
      }
      html.push('<ol style="padding-left:20px;margin:8px 0;">');
      items.forEach(item => {
        html.push(`<li style="margin:4px 0;color:#334155;">${formatInline(item)}</li>`);
      });
      html.push('</ol>');
      continue;
    }

    // --- LIGNE VIDE ---
    if (line.trim() === '') {
      html.push('<div style="height:8px;"></div>');
      i++;
      continue;
    }

    // --- PARAGRAPHE (fusion des lignes consécutives non-structurelles) ---
    const paragraphLines: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !isStructuralLine(lines[i])) {
      paragraphLines.push(lines[i].trim());
      i++;
    }
    if (paragraphLines.length > 0) {
      const mergedText = paragraphLines.join(' ');
      html.push(`<p style="margin:6px 0;line-height:1.6;color:#334155;">${formatInline(mergedText)}</p>`);
    }
  }

  return html.join('');
}
